import authService from './authService';
import type {
  AddressStopTime,
  DriverRouteLive,
  FleetRoutesDailyReport,
  FleetLiveVehicle,
  TrackerBatteryAlert,
  TrackerBatteryReport,
  TrackerEvent,
  TrackerRawMessage,
  UnknownTrackerDevice,
  VehicleTracker,
  VehicleStopsReport,
  VehicleTrack,
  VehicleTrackerFormData,
} from '../types/vehicleTracker';

class VehicleTrackerService {
  private async request<T>(method: 'get' | 'post' | 'put' | 'delete', path: string, fallback: string, body?: unknown): Promise<T> {
    const apiClient = await authService.getApiClient();
    const response = method === 'get' || method === 'delete' ? await apiClient[method](path) : await apiClient[method](path, body);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || fallback);
    }
    if (response.status === 204) return undefined as T;
    const json = await response.json();
    return json.data as T;
  }

  async list(): Promise<VehicleTracker[]> {
    return (await this.request<VehicleTracker[]>('get', '/trackers', 'Falha ao buscar rastreadores')) ?? [];
  }

  async listUnknown(): Promise<UnknownTrackerDevice[]> {
    return (await this.request<UnknownTrackerDevice[]>('get', '/trackers/unknown', 'Falha ao buscar aparelhos não cadastrados')) ?? [];
  }

  async create(data: VehicleTrackerFormData): Promise<VehicleTracker> {
    return this.request<VehicleTracker>('post', '/trackers', 'Falha ao cadastrar rastreador', data);
  }

  async update(id: number, data: VehicleTrackerFormData): Promise<VehicleTracker> {
    return this.request<VehicleTracker>('put', `/trackers/${id}`, 'Falha ao salvar rastreador', data);
  }

  async delete(id: number): Promise<void> {
    await this.request<void>('delete', `/trackers/${id}`, 'Falha ao excluir rastreador');
  }

  async listEvents(id: number, limit = 200): Promise<TrackerEvent[]> {
    return (await this.request<TrackerEvent[]>('get', `/trackers/${id}/events?limit=${limit}`, 'Falha ao buscar eventos')) ?? [];
  }

  /** Mensagens mais recentes primeiro; beforeId pagina para trás. */
  async listRawMessages(id: number, limit = 100, beforeId?: number): Promise<TrackerRawMessage[]> {
    const before = beforeId ? `&before_id=${beforeId}` : '';
    return (
      (await this.request<TrackerRawMessage[]>('get', `/trackers/${id}/raw-messages?limit=${limit}${before}`, 'Falha ao buscar mensagens')) ?? []
    );
  }

  /** Curva de bateria dos últimos `days` dias (padrão 7, até 60), estado e ciclos de descarga. */
  async battery(id: number, days = 7): Promise<TrackerBatteryReport> {
    return this.request<TrackerBatteryReport>('get', `/trackers/${id}/battery?days=${days}`, 'Falha ao buscar a bateria');
  }

  /** Rastreadores com bateria baixa ou desligados por bateria (sininho; fleet_tracking.read). */
  async batteryAlerts(): Promise<TrackerBatteryAlert[]> {
    return (await this.request<TrackerBatteryAlert[]>('get', '/fleet-tracking/battery-alerts', 'Falha ao buscar alertas de bateria')) ?? [];
  }

  /** Mapa da frota: veículos com rastreador ativo e o último estado. */
  async fleetLive(): Promise<FleetLiveVehicle[]> {
    return (await this.request<FleetLiveVehicle[]>('get', '/fleet-tracking/live', 'Falha ao buscar a posição da frota')) ?? [];
  }

  /** Trajeto do veículo; from/to em horário de Brasília (aaaa-mm-dd ou aaaa-mm-ddThh:mm). Sem período: hoje até agora. */
  async vehicleTrack(vehicleId: number, from?: string, to?: string): Promise<VehicleTrack> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return this.request<VehicleTrack>('get', `/fleet-tracking/vehicles/${vehicleId}/track${qs ? `?${qs}` : ''}`, 'Falha ao buscar o trajeto');
  }

  /** Paradas do veículo no período (até 31 dias), com totais. */
  async vehicleStops(vehicleId: number, from?: string, to?: string): Promise<VehicleStopsReport> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return this.request<VehicleStopsReport>('get', `/fleet-tracking/vehicles/${vehicleId}/stops${qs ? `?${qs}` : ''}`, 'Falha ao buscar as paradas');
  }

  /** Relatório diário das rotas pelo GPS (só admin). date = aaaa-mm-dd. */
  async routesDaily(date: string, driverId?: number, vehicleId?: number): Promise<FleetRoutesDailyReport> {
    return this.request<FleetRoutesDailyReport>('get', `/reports/fleet/routes-daily?${this.reportParams(date, driverId, vehicleId)}`, 'Falha ao gerar o relatório de rotas');
  }

  /** Tempo médio parado por endereço de entrega (até 31 dias). */
  async stopTimeByAddress(from: string, to: string): Promise<AddressStopTime[]> {
    const qs = new URLSearchParams({ from, to }).toString();
    return (await this.request<AddressStopTime[]>('get', `/reports/fleet/stop-time-by-address?${qs}`, 'Falha ao buscar o tempo por endereço')) ?? [];
  }

  /** Baixa o Excel do relatório diário (mesma sessão/cookie da API). */
  async downloadRoutesDailyExcel(date: string, driverId?: number, vehicleId?: number): Promise<void> {
    const { getConfig } = await import('../config');
    const url = `${getConfig().api.baseUrl}/reports/fleet/routes-daily/excel?${this.reportParams(date, driverId, vehicleId)}`;
    const doFetch = (token?: string | null) =>
      fetch(url, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} });
    let response = await doFetch();
    if (response.status === 401) response = await doFetch(await authService.getAuthToken(false));
    if (!response.ok || !(response.headers.get('content-type') ?? '').includes('spreadsheet')) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar Excel (${response.status})`);
    }
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rotas-gps-${date}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  private reportParams(date: string, driverId?: number, vehicleId?: number): string {
    const params = new URLSearchParams({ date });
    if (driverId) params.set('driver_id', String(driverId));
    if (vehicleId) params.set('vehicle_id', String(vehicleId));
    return params.toString();
  }

  /** Veículo e trajeto da rota, só durante ela. */
  async driverRouteLive(routeId: number): Promise<DriverRouteLive> {
    return this.request<DriverRouteLive>('get', `/driver-routes/${routeId}/live`, 'Falha ao buscar o veículo da rota');
  }
}

export const vehicleTrackerService = new VehicleTrackerService();
